/**
 * AddProcedureEditor — inline Add/Edit form for the Procedures chart
 * tab (PROD-827 chart write surfaces).
 *
 * Flutter ref: `patient_chart/.../tab_widgets/proc_tab.dart`.
 *
 * **Two modes, same form:**
 * - Add (`editing` omitted) → POST `/CreateProcedure` with
 *   `CreateProcedureBody`. Both `recorder` AND `asserter` populated
 *   with the current user. `dataAbsentReason: ""` (empty string).
 *   `createdDate: null`, `performerDetails: null` — server fills both.
 * - Edit (`editing` is a `PatientProcedure`) → PUT
 *   `/procedure/{id}` with `UpdateProcedureBody`. `recorder: null`
 *   on Update (NOT round-tripped, unlike Conditions/Allergies).
 *   `asserter` preserved from original record.
 *
 * **Procedures-specific wire quirks** (curl-verified 2026-05-20):
 * - `code.displayName` PascalCase (NOT `display`).
 * - `dataAbsentReason: ""` empty string on Create vs `"n/a"` on Update.
 * - `category` is a string label, not a Coding.
 * - `compilation` is a backend typo of `complication` — preserve.
 * - `outcome` + `compilation` default to `"n/a"` strings.
 * - `bodySite` + `reason` ship the empty-Coding sentinel on Create.
 */

import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Search } from 'lucide-react';
import { toast } from 'sonner';

import { Button, FieldLabel } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { TextArea as Textarea } from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useAuthStore } from '@/auth/auth-store';
import { useProcedureCodeSearch } from '@/patients/services/use-procedure-code-search';
import { useCreateProcedure } from '@/patients/services/use-create-procedure';
import { useUpdateProcedure } from '@/patients/services/use-update-procedure';
import { normalizePerformerDetails } from './add-allergy-dialog';
import {
  emptyProcedureCoding,
  normalizeProcedureCode,
} from '@/patients/lib/procedures';
import { DynamicDateInput } from './dynamic-date-input';
import { UNKNOWN_DATE_TEXT } from '@/encounter/lib/reported-date';
import {
  type CodeDisplayName,
  type CreateProcedureBody,
  type PatientProcedure,
  type UpdateProcedureBody,
} from '@/types/patient-chart-api';
import { V2_BODY_EMPHASIS, v2Look } from '@/shared/ui/design-v2';

// ──────────────────────────────────────────────────────────────────────────
// Zod schema
// ──────────────────────────────────────────────────────────────────────────

const schema = z
  .object({
    procedureJson: z.string().min(1, 'Pick a procedure'),
    // OPTIONAL (encounter owner ruling, 1 Nov 2026: the procedure date is not required). Blank,
    // and the rail's explicit "Unknown", carry no timestamp; only typed text that does not
    // resolve to a date is refused, so nothing typed is silently dropped.
    onsetDisplay: z.string().max(80),
    onsetIso: z.string().nullable(),
    note: z.string().max(500).optional(),
  })
  .refine((v) => v.onsetIso !== null || !v.onsetDisplay.trim() || v.onsetDisplay.trim() === UNKNOWN_DATE_TEXT, {
    message:
      'Procedure date must be a parseable date (DD/MM/YYYY, MM/YYYY, YYYY, or 1Y/2M/3D)',
    path: ['onsetDisplay'],
  });

type FormValues = z.infer<typeof schema>;

// ──────────────────────────────────────────────────────────────────────────
// Editor
// ──────────────────────────────────────────────────────────────────────────

export interface AddProcedureEditorProps {
  patientId: string;
  onClose: () => void;
  editing?: PatientProcedure | null;
}

export function AddProcedureEditor({
  patientId,
  onClose,
  editing = null,
}: AddProcedureEditorProps) {
  const isEditing = !!editing?.identifier;

  const user = useAuthStore((s) => s.user);
  const userId = user?.userId ?? '';

  const initialValues = useMemo<FormValues>(() => {
    if (!editing) {
      return {
        procedureJson: '',
        onsetDisplay: '',
        onsetIso: null,
        note: '',
      };
    }
    return {
      procedureJson: editing.code ? JSON.stringify(editing.code) : '',
      onsetDisplay: editing.onsetDateTimeDisplay ?? '',
      onsetIso: editing.onsetDateTime ?? null,
      note: editing.note ?? '',
    };
  }, [editing]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues,
  });

  const createMut = useCreateProcedure();
  const updateMut = useUpdateProcedure();
  const isPending = createMut.isPending || updateMut.isPending;

  const submit = form.handleSubmit(async (values) => {
    if (!user) {
      toast.error('Your session has expired. Please sign in again.');
      return;
    }
    if (!userId) {
      toast.error("Couldn't identify the recording user (missing userId).");
      return;
    }
    const procedure = safeParse<CodeDisplayName>(values.procedureJson);
    if (!procedure) {
      toast.error('Form values are corrupted — please re-pick the procedure.');
      return;
    }

    const resolvedOnset = values.onsetIso;

    try {
      if (isEditing && editing) {
        // Strict asserter guard — Procedures' asserter is the audit
        // trail of who originally recorded the procedure. Refuse to
        // ship null rather than substituting the editing user.
        if (!editing.asserter) {
          toast.error(
            "Cannot edit — this record is missing its original asserter. Please contact support.",
          );
          return;
        }
        const body: UpdateProcedureBody = {
          onsetDateTimeDisplay: values.onsetDisplay.trim() || null,
          identifier: editing.identifier!,
          partOf: null,
          clinicalStatus: clinicalStatusForEdit(editing.clinicalStatus),
          category: editing.category ?? null,
          code: normalizeProcedureCode(procedure),
          bodySite: emptyProcedureCoding(editing.bodySite),
          // Update ships "n/a" — distinct from Create's "" (curl-locked).
          dataAbsentReason: 'n/a',
          subject: patientId,
          // Recorder is NULL on Update (procedures wire quirk).
          recorder: null,
          encounter: editing.encounter ?? null,
          performer: null,
          asserter: editing.asserter,
          reasonReference: null,
          reason: emptyProcedureCoding(editing.reason),
          outcome: editing.outcome?.trim() || 'n/a',
          compilation: editing.compilation?.trim() || 'n/a',
          onsetDateTime: resolvedOnset,
          note: values.note?.trim() || '',
          isDeleted: false,
          performerDetails: normalizePerformerDetails(editing.performerDetails),
          createdDate: editing.createdDate ?? null,
          speciality: null,
        };
        await updateMut.mutateAsync({
          patientId,
          procedureId: editing.identifier!,
          body,
        });
        toast.success(
          `Updated procedure: ${procedure.displayName ?? procedure.code ?? 'Unknown'}`,
        );
      } else {
        const body: CreateProcedureBody = {
          onsetDateTimeDisplay: values.onsetDisplay.trim() || null,
          identifier: null,
          partOf: null,
          clinicalStatus: 'Active',
          category: null,
          code: normalizeProcedureCode(procedure),
          bodySite: emptyProcedureCoding(null),
          // Empty string on Create — Procedures-specific wire quirk.
          dataAbsentReason: '',
          subject: patientId,
          // Procedures populates BOTH recorder AND asserter on Create.
          recorder: userId,
          encounter: null,
          performer: null,
          asserter: userId,
          reasonReference: null,
          reason: emptyProcedureCoding(null),
          outcome: 'n/a',
          // Backend typo preserved verbatim.
          compilation: 'n/a',
          onsetDateTime: resolvedOnset,
          note: values.note?.trim() || '',
          isDeleted: false,
          performerDetails: null,
          createdDate: null,
          speciality: null,
        };
        await createMut.mutateAsync({ patientId, body });
        toast.success(
          `Added procedure: ${procedure.displayName ?? procedure.code ?? 'Unknown'}`,
        );
      }
      form.reset();
      onClose();
    } catch {
      /* Global toast surfaces failure; keep editor open. */
    }
  });

  return (
    // The design's PaEditorCard: the one card (hairline, 12 corners, white, no shadow, 24 padding); look in
    // showcase-patient.css `editor card`.
    <div data-slot="editor-card">
      <form onSubmit={submit} className="space-y-6">
        <Controller
          name="procedureJson"
          control={form.control}
          render={({ field, fieldState }) => (
            <ProcedurePicker
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="onsetIso"
          control={form.control}
          render={({ field: isoField }) => (
            <Controller
              name="onsetDisplay"
              control={form.control}
              render={({ field: displayField, fieldState }) => (
                <DynamicDateInput
                  label="Procedure date"
                  displayValue={displayField.value}
                  isoValue={isoField.value}
                  onChange={(next) => {
                    displayField.onChange(next.displayValue);
                    isoField.onChange(next.isoValue);
                  }}
                  error={fieldState.error?.message}
                />
              )}
            />
          )}
        />

        <div>
          <FieldLabel
            htmlFor="note"
            
          >
            Note
          </FieldLabel>
          <Textarea
            id="note"
            {...form.register('note')}
            placeholder="Optional context, e.g. setting, intra-operative findings…"
            rows={3}
            className="mt-1"
          />
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button type="submit" disabled={isPending}>
            {isPending ? (
              <>
                Saving…
              </>
            ) : isEditing ? (
              'Update'
            ) : (
              'Done'
            )}
          </Button>
          <Button
            type="button"
            variant="tertiary"
            onClick={() => {
              form.reset();
              onClose();
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// ProcedurePicker — CPT typeahead
// ──────────────────────────────────────────────────────────────────────────

function ProcedurePicker({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (next: string) => void;
  error?: string;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 250);
  const searchQ = useProcedureCodeSearch(debounced, true);

  const selected = useMemo<CodeDisplayName | null>(
    () => (value ? safeParse<CodeDisplayName>(value) : null),
    [value],
  );

  const results = searchQ.data ?? [];

  return (
    <div>
      <FieldLabel
        htmlFor="procedure"
        
      >
        Procedure <span className="text-red-700">*</span>
      </FieldLabel>

      {selected ? (
        (() => {
          // Defensive re-normalize in case a legacy stored value still
          // carries the wrapped master-row shape — `normalizeProcedureCode`
          // unwraps `{code: Coding}` and guarantees string fields.
          const flat = normalizeProcedureCode(selected);
          const codeStr = flat?.code ?? '';
          const nameStr = flat?.displayName?.trim() || '';
          return (
            <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-border-default bg-blue-100/30 px-3 py-2">
              <div className="min-w-0">
                <p className={v2Look('text-sm font-medium text-brand-depth truncate', V2_BODY_EMPHASIS)}>
                  {nameStr || codeStr || '—'}
                </p>
                {codeStr ? (
                  <p className="text-xs text-text-hint">
                    {codeStr}
                    {flat?.description ? ` · ${flat.description}` : ''}
                  </p>
                ) : null}
              </div>
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                onClick={() => {
                  onChange('');
                  setQuery('');
                }}
              >
                Change
              </Button>
            </div>
          );
        })()
      ) : (
        <>
          <div className="relative mt-1">
            <Search
              aria-hidden
              className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-hint"
            />
            <TextField
              id="procedure"
              type="text"
              placeholder="Type to search CPT procedures (e.g. 4079F, rehab)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              inputClassName="pl-10"
              autoComplete="off"
            />
          </div>

          {debounced.trim().length >= 2 ? (
            <div className="mt-1 max-h-56 overflow-y-auto scrollbar-hide rounded-md border border-border-default bg-surface-card divide-y divide-border-default">
              {searchQ.isFetching ? (
                <div className="px-3 py-2 text-sm text-text-muted">
                  Searching…
                </div>
              ) : results.length === 0 ? (
                <div className="px-3 py-2 text-sm text-text-muted">
                  No matches. Try another word or a code.
                </div>
              ) : (
                results.map((row, idx) => {
                  // Master endpoint returns flat `{code, displayName, system,
                  // description, shortName}` rows. Defensive normalization in
                  // case the wire ever ships wrapped (same bug class as the
                  // DiagnosisCodes 400 — PR #225 commit 9295a3f).
                  const flat = normalizeProcedureCode(row);
                  if (!flat) return null;
                  const codeStr = flat.code ?? '';
                  const nameStr = flat.displayName?.trim() || '';
                  const rowKey = `${codeStr}-${nameStr}-${idx}`;
                  return (
                    <button
                      key={rowKey}
                      type="button"
                      onClick={() => {
                        onChange(JSON.stringify(flat));
                        setQuery('');
                      }}
                      className="block w-full px-3 py-2 text-left hover:bg-grey-100/50"
                    >
                      <span className="text-sm text-text-primary block">
                        {nameStr || codeStr || '—'}
                      </span>
                      <span className="text-xs text-text-hint">
                        {codeStr}
                        {flat.description ? ` · ${flat.description}` : ''}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          ) : null}
        </>
      )}

      {error ? (
        <p className="text-xs text-red-700 mt-1" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

function safeParse<T>(s: string): T | null {
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

function clinicalStatusForEdit(
  status: string | null | undefined,
): 'Active' | 'Resolved' {
  return status === 'Resolved' ? 'Resolved' : 'Active';
}
